import { normalizeTicketStatusCode } from './manualMatrixEscalationEligibility';
import { isSlaPausedStatus, isTerminalSlaStatus } from './slaCountdown';

/** Mirrors backend SlaEscalationProcessor outcome strings. */
export const AUTO_ESCALATE_OUTCOMES = Object.freeze({
  ESCALATED: 'ESCALATED',
  NOT_DUE: 'NOT_DUE',
  NOT_ELIGIBLE: 'NOT_ELIGIBLE',
  UNCHANGED: 'UNCHANGED',
});

/** Align with backend AUTO_ESCALATION_BLOCKED_STATUS — reduces pointless API calls. */
const AUTO_ESCALATION_BLOCKED_STATUS_CODES = new Set([
  'resolved',
  'closed',
  'escalated',
  'pending_customer',
  'pending_customer_response',
  'pending_supervisor_approval',
  'supervisor_approved',
]);

/** Min interval between attempts for the same ticket + calendar deadline. */
export const AUTO_ESCALATE_RETRY_INTERVAL_MS = 60_000;

/** Recheck long deadlines in 24h chunks (setTimeout-safe, not too frequent). */
export const MAX_DUE_WAKE_CHUNK_MS = 86_400_000;

/** Upper bound for setTimeout delay in browsers (~24.8 days). */
export const MAX_SAFE_SET_TIMEOUT_MS = 2_147_483_647;

export function parseCalendarDueDateMs(dueDate) {
  if (dueDate == null || dueDate === '') {
    return null;
  }
  const ms = Date.parse(String(dueDate));
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Matrix auto-escalation uses persisted calendar due_date (wall clock), not D1 business remaining.
 */
export function isCalendarDueDateReached(dueDate, nowMs = Date.now()) {
  const dueMs = parseCalendarDueDateMs(dueDate);
  if (dueMs == null) {
    return false;
  }
  return nowMs >= dueMs;
}

export function buildAutoEscalateAttemptKey(ticketId, dueDate) {
  return `${ticketId}:${String(dueDate ?? '')}`;
}

/** Milliseconds until calendar dueDate; null when dueDate invalid. */
export function msUntilCalendarDue(dueDate, nowMs = Date.now()) {
  const dueMs = parseCalendarDueDateMs(dueDate);
  if (dueMs == null) {
    return null;
  }
  return dueMs - nowMs;
}

/**
 * Next delay before rechecking a future deadline (chunked for long windows).
 * Returns 0 when already due, null when dueDate invalid.
 */
export function nextDueWakeDelayMs(
  dueDate,
  nowMs = Date.now(),
  chunkMs = MAX_DUE_WAKE_CHUNK_MS
) {
  const remaining = msUntilCalendarDue(dueDate, nowMs);
  if (remaining == null) {
    return null;
  }
  if (remaining <= 0) {
    return 0;
  }
  const safeChunk = Math.min(chunkMs, MAX_SAFE_SET_TIMEOUT_MS);
  return Math.min(remaining, safeChunk);
}

/** For tests: chunk schedule until calendar due is reached. */
export function planDueWakeDelaysUntilDue(dueDate, nowMs, maxSteps = 16) {
  const delays = [];
  let cursor = nowMs;
  for (let step = 0; step < maxSteps; step += 1) {
    const delay = nextDueWakeDelayMs(dueDate, cursor);
    if (delay == null || delay <= 0) {
      break;
    }
    delays.push(delay);
    cursor += delay;
  }
  return delays;
}

export function shouldApplyEscalationTicketRefresh(
  ticketLoadGenAtStart,
  currentTicketLoadGen,
  effectGenAtStart,
  currentEffectGen
) {
  return (
    ticketLoadGenAtStart === currentTicketLoadGen
    && effectGenAtStart === currentEffectGen
  );
}

export function isAutoEscalationFrontendCandidate(ticket, statuses) {
  if (!ticket) {
    return false;
  }
  if (ticket.slaApplicable !== true || !ticket.dueDate) {
    return false;
  }
  if (!ticket.assignedTime && !ticket.assigned_time) {
    return false;
  }
  if (ticket.assigneeId == null && ticket.assignee_id == null) {
    return false;
  }
  if (isTerminalSlaStatus(ticket, statuses) || isSlaPausedStatus(ticket, statuses)) {
    return false;
  }
  const statusCode = normalizeTicketStatusCode(ticket);
  if (AUTO_ESCALATION_BLOCKED_STATUS_CODES.has(statusCode)) {
    return false;
  }
  return true;
}

/**
 * @returns {{ canAttempt: boolean, attemptKey: string|null }}
 */
export function evaluateAutoEscalateAttemptGuard(guard, ticketId, dueDate, nowMs = Date.now()) {
  const attemptKey = buildAutoEscalateAttemptKey(ticketId, dueDate);
  if (guard.inFlight) {
    return { canAttempt: false, attemptKey };
  }
  if (
    guard.attemptKey === attemptKey
    && nowMs - guard.lastAttemptAt < AUTO_ESCALATE_RETRY_INTERVAL_MS
  ) {
    return { canAttempt: false, attemptKey };
  }
  return { canAttempt: true, attemptKey };
}

export function createInitialAutoEscalateGuard() {
  return {
    inFlight: false,
    attemptKey: null,
    lastAttemptAt: 0,
  };
}
